import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { EpisodeService, SettingsService } from '../../core/services';
import { Episode } from '../../core/models';
import { environment } from '../../../environments/environment';
import { AudioPlayerComponent } from '../../shared/components';

@Component({
  selector: 'app-episode-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, AudioPlayerComponent],
  templateUrl: './episode-detail.component.html',
  styleUrl: './episode-detail.component.scss'
})
export class EpisodeDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  episode: Episode | null = null;
  isLoading = true;
  error: string | null = null;
  defaultCoverUrl: string | null = null;
  spreakerPlayerUrl: SafeResourceUrl | null = null;

  constructor(
    private episodeService: EpisodeService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private router: Router,
    private meta: Meta,
    private title: Title,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // Carica le impostazioni per la copertina di default
    this.settingsService.getPublicSettings().subscribe({
      next: (response) => {
        if (response.data.default_cover) {
          // Costruisci l'URL base rimuovendo /api dalla fine
          let baseUrl = environment.apiUrl;
          if (baseUrl.endsWith('/api')) {
            baseUrl = baseUrl.slice(0, -4);
          }
          
          const coverPath = response.data.default_cover;
          if (coverPath.startsWith('/')) {
            this.defaultCoverUrl = `${baseUrl}${coverPath}`;
          } else {
            this.defaultCoverUrl = `${baseUrl}/media/covers/${coverPath}`;
          }
        }
      },
      error: (err) => console.error('Error loading settings:', err)
    });

    // Carica l'episodio quando cambia lo slug
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const slug = params['slug'];
        if (slug) {
          this.loadEpisode(slug);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadEpisode(slug: string): void {
    this.isLoading = true;
    this.error = null;

    this.episodeService.getBySlug(slug)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('📺 Episode loaded:', response);
          this.episode = this.mapEpisodeFromBackend(response.data);
          this.spreakerPlayerUrl = this.getSpreakerPlayerUrl(this.episode.spreakerEpisodeId);
          
          // Aggiorna i meta tag con i dati dell'episodio
          if (this.episode) {
            const pageTitle = `${this.episode.title} - AI PodLab`;
            const description = this.episode.description || 'Ascolta questo episodio del podcast AI PodLab su scienza e tecnologia.';
            const coverUrl = this.getCoverImageUrl(this.episode.coverImage);
            
            this.title.setTitle(pageTitle);
            this.meta.updateTag({ name: 'description', content: description });
            this.meta.updateTag({ property: 'og:title', content: pageTitle });
            this.meta.updateTag({ property: 'og:description', content: description });
            this.meta.updateTag({ property: 'og:type', content: 'article' });
            this.meta.updateTag({ property: 'og:image', content: coverUrl });
            
            // Tag Twitter Card
            this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
            this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
            this.meta.updateTag({ name: 'twitter:description', content: description });
            this.meta.updateTag({ name: 'twitter:image', content: coverUrl });
          }
          
          this.isLoading = false;
        },
        error: (err) => {
          console.error('❌ Error loading episode:', err);
          this.error = 'Impossibile caricare l\'episodio. Riprova più tardi.';
          this.isLoading = false;
        }
      });
  }

  /**
   * Mappa i dati dal formato backend al formato frontend
   */
  private mapEpisodeFromBackend(backendData: any): Episode {
    // Parse tags e categories
    let tags: any[] = [];
    let categories: any[] = [];

    if (backendData.tag_names) {
      const tagNames = typeof backendData.tag_names === 'string' 
        ? backendData.tag_names.split(',').map((t: string) => t.trim())
        : backendData.tag_names;
      
      const tagSlugs = backendData.tag_slugs
        ? (typeof backendData.tag_slugs === 'string'
          ? backendData.tag_slugs.split(',').map((t: string) => t.trim())
          : backendData.tag_slugs)
        : [];

      tags = tagNames.map((name: string, index: number) => ({
        name,
        slug: tagSlugs[index] || name
      }));
    }

    if (backendData.category_names) {
      const categoryNames = typeof backendData.category_names === 'string'
        ? backendData.category_names.split(',').map((c: string) => c.trim())
        : [backendData.category_names];
      
      const categorySlugs = backendData.category_slug
        ? (typeof backendData.category_slug === 'string'
          ? backendData.category_slug.split(',').map((c: string) => c.trim())
          : [backendData.category_slug])
        : [];

      categories = categoryNames.map((name: string, index: number) => ({
        name,
        slug: categorySlugs[index] || name
      }));
    }

    return {
      id: backendData.id,
      title: backendData.title,
      slug: backendData.slug,
      description: backendData.description,
      longDescription: backendData.long_description || backendData.content,
      audioUrl: this.getAudioUrl(backendData.audio_file),
      spreakerEpisodeId: backendData.spreaker_episode_id || undefined,
      duration: backendData.audio_duration || 0,
      publishedAt: new Date(backendData.published_at),
      coverImage: backendData.cover_image,
      categoryId: backendData.category_id || 0,
      category: categories[0],
      tags: tags,
      seasonNumber: backendData.season_number,
      episodeNumber: backendData.episode_number,
      isPublished: backendData.is_published,
      createdAt: new Date(backendData.created_at),
      updatedAt: new Date(backendData.updated_at)
    };
  }

  /**
   * URL del widget Spreaker per l'episodio (null se non configurato o non valido)
   */
  private getSpreakerPlayerUrl(episodeId: string | undefined): SafeResourceUrl | null {
    if (!episodeId || !/^\d+$/.test(episodeId)) {
      return null;
    }
    const url = `https://widget.spreaker.com/player?episode_id=${episodeId}&theme=light&playlist=show&playlist-continuous=true&hide-download=true`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Ottiene l'URL completo dell'immagine di copertina
   * @param coverImage Nome file immagine
   * @param size Dimensione thumbnail: 'small' | 'medium' | 'large' | undefined (original)
   */
  getCoverImageUrl(coverImage: string | undefined, size?: 'small' | 'medium' | 'large'): string {
    if (!coverImage) {
      if (this.defaultCoverUrl) {
        return this.defaultCoverUrl;
      }
      return 'https://via.placeholder.com/1200x675/667eea/ffffff?text=PodLab';
    }
    if (coverImage.startsWith('http')) {
      return coverImage;
    }
    
    // Costruisci l'URL base rimuovendo /api dalla fine
    let baseUrl = environment.apiUrl;
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    }
    
    // Se richiede un thumbnail specifico
    if (size) {
      const ext = coverImage.substring(coverImage.lastIndexOf('.'));
      const nameWithoutExt = coverImage.substring(0, coverImage.lastIndexOf('.'));
      const thumbnailPath = `thumbs/${nameWithoutExt}-${size}${ext}`;
      
      if (thumbnailPath.startsWith('/')) {
        return `${baseUrl}${thumbnailPath}`;
      }
      return `${baseUrl}/media/covers/${thumbnailPath}`;
    }
    
    // Altrimenti usa l'originale
    if (coverImage.startsWith('/')) {
      return `${baseUrl}${coverImage}`;
    }
    return `${baseUrl}/media/covers/${coverImage}`;
  }

  getAudioUrl(audioFile: string | undefined): string {
    if (!audioFile) {
      return '';
    }
    if (audioFile.startsWith('http')) {
      return audioFile;
    }
    
    // Costruisci l'URL base rimuovendo /api dalla fine
    let baseUrl = environment.apiUrl;
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    }
    
    let audioUrl = '';
    if (audioFile.startsWith('/')) {
      audioUrl = `${baseUrl}${audioFile}`;
    } else {
      // I file audio sono nella stessa cartella degli episodi
      audioUrl = `${baseUrl}/media/episodes/${audioFile}`;
    }
    console.log('🎵 Audio URL generated:', audioUrl);
    return audioUrl;
  }

  formatDuration(seconds: number): string {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  formatDate(date: Date | string): string {
    if (!date) {
      return 'Data non disponibile';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Data non disponibile';
    }
    return dateObj.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  goBack(): void {
    this.router.navigate(['/episodi']);
  }
}
